import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Trash2, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

export type LLMParameterType = 'string' | 'int' | 'float' | 'bool' | 'enum' | 'array';

export interface LLMParameterDefinition {
  name: string;
  type: LLMParameterType;
  nullable: boolean;
  min?: number | null;
  max?: number | null;
  default?: string | number | boolean | Array<string | number | boolean> | null;
  description?: string;
  /** Allowed choices for `enum` type (single choice). */
  allowedValues?: Array<string | number>;
  /** Element type for `array` type. */
  itemType?: 'string' | 'int' | 'float';
  /**
   * Kundensichtbarkeit (CA-627): `false` hält den Parameter aus der
   * Kunden-Agent-Maske heraus (nur Admin-Flächen/E1). Fehlt das Attribut,
   * gilt sichtbar; der Editor persistiert beim Speichern immer explizit.
   */
  isCustomerVisibleParameter?: boolean;
}

/**
 * Typen der wohlbekannten Sampling-Parameter. Das Legacy-Format
 * (`supportedParameters` als String-Array) trug keinen Typ — ohne diese Map
 * wuerde beim Heben aus `max_tokens` ein Float und die Validierung liesse
 * Nachkommastellen durch. Unbekannte Namen fallen auf `float` zurueck.
 */
const LEGACY_PARAMETER_TYPES: Record<string, LLMParameterType> = {
  temperature: 'float',
  max_tokens: 'int',
  top_p: 'float',
  top_k: 'int',
  frequency_penalty: 'float',
  presence_penalty: 'float',
};

interface ParameterEditorProps {
  parameters: LLMParameterDefinition[];
  onChange: (parameters: LLMParameterDefinition[]) => void;
  title?: string;
  description?: string;
  allowAdd?: boolean;
  allowRemove?: boolean;
}

export function ParameterEditor({
  parameters,
  onChange,
  title = 'Parameter Configuration',
  description = 'Configure LLM parameters with types, constraints, and defaults',
  allowAdd = true,
  allowRemove = true,
}: ParameterEditorProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  /**
   * Hebt Legacy-Einträge (`supportedParameters` als reines String-Array) auf
   * Definitionsobjekte und macht die Kundensichtbarkeit explizit, damit jeder
   * Save den Wert mitpersistiert statt ihn implizit zu lassen.
   *
   * Der String-Zweig ist Pflicht: ein `{ ...'temperature' }` würde den Eintrag
   * in ein Zeichen-Index-Objekt verwandeln und den Katalog des Providers
   * zerstören (`param.name` wäre `undefined`, die Auflösung kollabiert).
   */
  const normalizeParameters = (
    params: Array<LLMParameterDefinition | string>,
  ): LLMParameterDefinition[] =>
    params.map((p) =>
      typeof p === 'string'
        ? {
            name: p,
            type: LEGACY_PARAMETER_TYPES[p] ?? 'float',
            nullable: false,
            isCustomerVisibleParameter: true,
          }
        : { ...p, isCustomerVisibleParameter: p.isCustomerVisibleParameter !== false },
    );

  /**
   * Anzeige- und Bearbeitungsbasis. Legacy-String-Einträge werden hier einmal
   * gehoben, damit die Felder gefüllt sind — geschrieben wird erst bei einem
   * echten Edit, ein reines Öffnen des Dialogs ändert nichts.
   */
  const visibleParameters = normalizeParameters(
    parameters as Array<LLMParameterDefinition | string>,
  );

  const handleAdd = () => {
    const newParam: LLMParameterDefinition = {
      name: '',
      type: 'float',
      nullable: true,
      min: null,
      max: null,
      default: null,
      description: '',
      isCustomerVisibleParameter: true,
    };
    onChange([...visibleParameters, newParam]);
    setEditingIndex(visibleParameters.length);
  };

  const handleRemove = (index: number) => {
    onChange(visibleParameters.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
    }
  };

  const handleUpdate = (index: number, field: keyof LLMParameterDefinition, value: any) => {
    const updated = [...visibleParameters];
    updated[index] = { ...updated[index], [field]: value };
    onChange(normalizeParameters(updated));
  };

  const getDefaultValueInput = (param: LLMParameterDefinition, index: number) => {
    switch (param.type) {
      case 'bool':
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={param.default === true}
              onCheckedChange={(checked) => handleUpdate(index, 'default', checked ? true : null)}
            />
            <Label>Default: {param.default === true ? 'true' : param.default === false ? 'false' : 'null'}</Label>
          </div>
        );
      case 'int':
      case 'float':
        return (
          <Input
            type="number"
            step={param.type === 'float' ? '0.01' : '1'}
            value={typeof param.default === 'number' ? param.default : ''}
            onChange={(e) => {
              const val = e.target.value === '' ? null : param.type === 'int' ? parseInt(e.target.value) : parseFloat(e.target.value);
              handleUpdate(index, 'default', val);
            }}
            placeholder="Default value"
          />
        );
      case 'enum': {
        const options = param.allowedValues ?? [];
        return (
          <Select
            value={param.default != null ? String(param.default) : ''}
            onValueChange={(value) => handleUpdate(index, 'default', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder={options.length ? 'Select default' : 'Define allowed values first'} />
            </SelectTrigger>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={String(opt)} value={String(opt)}>
                  {String(opt) === '' ? '(empty)' : String(opt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }
      case 'array':
        return (
          <CommaSeparatedInput
            values={Array.isArray(param.default) ? param.default : []}
            onChange={(items) => handleUpdate(index, 'default', items)}
            transform={(s) => (param.itemType === 'int' ? parseInt(s) : param.itemType === 'float' ? parseFloat(s) : s)}
            placeholder="Comma-separated default items"
          />
        );
      case 'string':
        return (
          <Input
            type="text"
            value={param.default as string ?? ''}
            // Empty string is a valid value — keep '' instead of coercing to null.
            onChange={(e) => handleUpdate(index, 'default', e.target.value)}
            placeholder="Default value (empty string allowed)"
          />
        );
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {parameters.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No parameters configured yet
            </div>
          ) : (
            visibleParameters.map((param, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Parameter Name</Label>
                      <Input
                        value={param.name}
                        onChange={(e) => handleUpdate(index, 'name', e.target.value)}
                        placeholder="e.g. temperature"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select
                        value={param.type}
                        onValueChange={(value) => handleUpdate(index, 'type', value as LLMParameterType)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">String</SelectItem>
                          <SelectItem value="int">Integer</SelectItem>
                          <SelectItem value="float">Float</SelectItem>
                          <SelectItem value="bool">Boolean</SelectItem>
                          <SelectItem value="enum">Enum (Select)</SelectItem>
                          <SelectItem value="array">Array (List)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {allowRemove && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(index)}
                      className="ml-2 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>

                {(param.type === 'int' || param.type === 'float' || param.type === 'array') && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{param.type === 'array' ? 'Min Items' : 'Min Value'}</Label>
                      <Input
                        type="number"
                        step={param.type === 'float' ? '0.01' : '1'}
                        value={param.min ?? ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? null : param.type === 'float' ? parseFloat(e.target.value) : parseInt(e.target.value);
                          handleUpdate(index, 'min', val);
                        }}
                        placeholder={param.type === 'array' ? 'Min item count' : 'Min value'}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{param.type === 'array' ? 'Max Items' : 'Max Value'}</Label>
                      <Input
                        type="number"
                        step={param.type === 'float' ? '0.01' : '1'}
                        value={param.max ?? ''}
                        onChange={(e) => {
                          const val = e.target.value === '' ? null : param.type === 'float' ? parseFloat(e.target.value) : parseInt(e.target.value);
                          handleUpdate(index, 'max', val);
                        }}
                        placeholder={param.type === 'array' ? 'Max item count' : 'Max value'}
                      />
                    </div>
                  </div>
                )}

                {param.type === 'enum' && (
                  <div className="space-y-2">
                    <Label>Allowed Values (comma-separated)</Label>
                    <CommaSeparatedInput
                      values={param.allowedValues ?? []}
                      onChange={(values) => handleUpdate(index, 'allowedValues', values)}
                      transform={(s) => (/^-?\d+(\.\d+)?$/.test(s) ? Number(s) : s)}
                      placeholder="e.g. minimal, low, medium, high"
                    />
                  </div>
                )}

                {param.type === 'array' && (
                  <div className="space-y-2">
                    <Label>Item Type</Label>
                    <Select
                      value={param.itemType ?? 'string'}
                      onValueChange={(value) => handleUpdate(index, 'itemType', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="string">String</SelectItem>
                        <SelectItem value="int">Integer</SelectItem>
                        <SelectItem value="float">Float</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Default Value</Label>
                    {getDefaultValueInput(param, index)}
                  </div>
                  <div className="flex items-center space-x-2 pt-7">
                    <Switch
                      checked={param.nullable}
                      onCheckedChange={(checked) => handleUpdate(index, 'nullable', checked)}
                    />
                    <Label>Nullable</Label>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    checked={param.isCustomerVisibleParameter !== false}
                    onCheckedChange={(checked) => handleUpdate(index, 'isCustomerVisibleParameter', checked)}
                  />
                  <Label>Customer visible</Label>
                  <span className="text-xs text-gray-500">
                    Off = nur Admin-Flächen &amp; Bot-Runtime (z.B. reasoning_effort), nicht in der Kunden-Agent-Maske
                  </span>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    value={param.description ?? ''}
                    onChange={(e) => handleUpdate(index, 'description', e.target.value || undefined)}
                    placeholder="Optional description"
                  />
                </div>
              </div>
            ))
          )}

          {allowAdd && (
            <Button onClick={handleAdd} variant="outline" className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Parameter
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface CommaSeparatedInputProps {
  values: Array<string | number | boolean>;
  onChange: (values: Array<string | number>) => void;
  /** Maps a trimmed token to its typed value (e.g. numeric coercion). */
  transform?: (token: string) => string | number;
  placeholder?: string;
}

/**
 * Comma-separated list input backed by its own raw-text state, so the user can
 * freely type commas and spaces. The parsed array is emitted via `onChange`,
 * but the displayed value is the raw text (not the re-joined array) — otherwise
 * every typed comma would be stripped on the round-trip.
 */
function CommaSeparatedInput({ values, onChange, transform, placeholder }: CommaSeparatedInputProps) {
  const [text, setText] = useState(values.join(', '));
  const map = transform ?? ((s: string) => s);

  return (
    <Input
      type="text"
      value={text}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const parsed = raw
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
          .map(map);
        onChange(parsed);
      }}
      placeholder={placeholder}
    />
  );
}
